import React, { createContext, useContext, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { BsFolderPlus } from '@react-icons/all-files/bs/BsFolderPlus';
import { useContextSelector } from 'use-context-selector';

import { EditorPlaceholder } from '../../components/EditorPlaceholder';
import { EditorContext } from '../../contexts';
import { DocumentPath } from '../../types';
import { createId, findNode } from '../../utils';
import { TabItemProps } from './TabItem/TabItem';

interface TabContextValue {
  id: string;
  currentIndex: number;
  selectIndex: (index: number) => void;
}

const TabContext = createContext({} as TabContextValue);
export const useTabContext = () => useContext(TabContext);

export interface TabProps {
  children: React.ReactNode;
  path: DocumentPath;
  buttonColumn: number;
}

export function Tab({ children, path, buttonColumn }: TabProps) {
  const [id, setId] = useState<string | null>(null);
  const [currentIndex, selectIndex] = useState(0);

  useEffect(() => {
    setId(createId());
  }, []);

  useEditorInitializer({
    path,
    currentIndex,
    selectIndex,
  });

  if (!children || !id) {
    return (
      <EditorPlaceholder icon={<BsFolderPlus />} message="탭을 추가해주세요." />
    );
  }

  return (
    <TabContext.Provider
      value={{
        id,
        currentIndex,
        selectIndex,
      }}
    >
      <Buttons column={buttonColumn}>
        { React.Children.map(children, (child, index) => {
          if (!React.isValidElement(child)) { return child; }
          return React.cloneElement(child, { index } as TabItemProps);
        }) }
      </Buttons>
      <div id={`tab-${id}-panel`} />
    </TabContext.Provider>
  );
}

function useEditorInitializer({
  path,
  currentIndex = -1,
  selectIndex,
}: { path: DocumentPath } & Partial<TabContextValue>) {
  const context = useContextSelector(EditorContext, (context) => context);
  const node = context ? findNode(context.state.document, path) : undefined;

  function handleNodeDelete() {
    if (!context || !node) { return; }
    if (currentIndex < node.children.length) { return; }
    selectIndex?.(0);
  }
  useEffect(handleNodeDelete, [node?.children.length]);
}

const Buttons = styled.div<{ column: number }>`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  > * {
    ${({ column }) => `flex: 0 0 ${100 / column}%;`}
    margin: 0;
    border: 0;
  }
`;
